import React, { useState } from 'react';
import { FileText, Trophy, Clock, Film, Users, AlertTriangle } from 'lucide-react';


import type { ReactNode } from 'react';
type RuleContentItem =
  | string
  | { subtitle: string; text: string }
  | { highlight: true; text: ReactNode };

type RuleSection = {
  id: string;
  title: string;
  icon: JSX.Element;
  content: RuleContentItem[];
};

// Add custom CSS variables for the Official Rules page
// eslint-disable-next-line
const customColors = {
  '--primary-grey': '#6B7280',
  '--secondary-grey': '#9CA3AF',
  '--dark-grey': '#374151',
  '--light-grey': '#F3F4F6',
};

const OfficialRules = () => {
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});
  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const sections: RuleSection[] = [
    {
      id: 'eligibility',
      title: 'ELIGIBILITY',
      icon: <Users className="w-5 h-5" />,
      content: [
        'All participants must have an active account on the platform with the "Become an Uploader" option enabled and at least 1 remaining video upload chance',
        'There are no geographical restrictions or age requirements unless otherwise specified.',
        'Participants may enter individually or as a team. However, all team entries must be submitted through a single account with the "Become an Uploader" option enabled.'
      ]
    },
    {
      id: 'submission',
      title: 'SUBMISSION PERIOD',
      icon: <Clock className="w-5 h-5" />,
      content: [
        'The competition officially begins at 12:00 AM on Aug 15th and ends at 12:00 AM on May 31st 2026.',
        'Only videos uploaded within this specific timeframe will be considered for the competition. Videos uploaded before the launch date or after the end date will not be eligible.',
        'All entries must be fully uploaded to the platform before the submission deadline. Incomplete uploads at the deadline will not be considered.',
        'The platform\'s server time will be the official timekeeper for the competition.'
      ]
    },
    {
      id: 'requirements',
      title: 'FILM REQUIREMENTS',
      icon: <Film className="w-5 h-5" />,
      content: [
        {
          subtitle: 'Duration',
          text: 'All submitted films must be between 8 minutes (minimum) and 15 minutes (maximum) in length, including opening and closing credits.'
        },
        {
          subtitle: 'Technical Specifications',
          text: 'Recommended bitrate: 10 Mbps • Resolution: Minimum 1080p (1920×1080) up to 4K (3840×2160) • Acceptable file format: MP4 only'
        },
        'Films must be original content created by the submitting participant(s).',
        'All films must be primarily in the Tamil language. Subtitles in other languages are permitted but not required.',
        'All submitted content must comply with the platform\'s general terms of service and community guidelines.'
      ]
    },
    {
      id: 'judging',
      title: 'JUDGING CRITERIA',
      icon: <FileText className="w-5 h-5" />,
      content: [
        'Submissions will be evaluated based on two specific metrics: Most viewed submissions within the competition duration • Highest rated among the top 10 most viewed submissions in the competition period',
        'All views and ratings will be tracked through the platform\'s built-in analytics system.',
        'Only views and ratings accumulated during the official competition period will count toward the final judgment.'
      ]
    },
    {
      id: 'prizes',
      title: 'PRIZES AND RECOGNITION',
      icon: <Trophy className="w-5 h-5" />,
      content: [
        'The competition offers two prize categories:',
          { highlight: true, text: <span style={{ fontWeight: 'bold', color: '#C026D3', background: '#F3F4F6', padding: '4px 8px', borderRadius: '6px', boxShadow: '0 2px 8px #C026D340' }}>
            TOP Live Winner: <span style={{ color: '#16A34A', fontWeight: 'bold' }}>₹25,000</span> awarded to the most viewed submission during the competition period
          </span> },
          { highlight: true, text: <span style={{ fontWeight: 'bold', color: '#2563EB', background: '#F3F4F6', padding: '4px 8px', borderRadius: '6px', boxShadow: '0 2px 8px #2563EB40' }}>
            TOP Live Best Rated: <span style={{ color: '#F59E42', fontWeight: 'bold' }}>₹25,000</span> awarded to the highest rated submission among the top 10 most viewed entries
          </span> },
        'Winners will be determined based on data captured at exactly 11:59 PM on the end date of the competition.',
        'The platform\'s analytics system will be the sole determinant for view counts and ratings.'
      ]
    },
    {
      id: 'intellectual',
      title: 'INTELLECTUAL PROPERTY',
      icon: <FileText className="w-5 h-5" />,
      content: [
        'Participants retain the copyright to their submitted work.',
        'By entering the competition, participants grant the platform a non-exclusive license to display, promote, and share their work in relation to the competition.',
        'Participants are responsible for securing all necessary rights, licenses, and permissions for any third-party content included in their submissions.'
      ]
    },
    {
      id: 'disqualification',
      title: 'DISQUALIFICATION',
      icon: <AlertTriangle className="w-5 h-5" />,
      content: [
        'Submissions that do not adhere to the specified technical requirements will not be considered.',
        'Any film containing plagiarized content, unauthorized copyrighted material, or offensive content as determined by the competition organizers will be disqualified.',
        'Attempts to manipulate the voting or judging process will result in immediate disqualification.',
        'Profane Language and vulgarity in videos will lead to immediate disqualification, Kolandium organization reserves the right to determine these factors.'
      ]
    },
    {
      id: 'additional',
      title: 'ADDITIONAL TERMS',
      icon: <FileText className="w-5 h-5" />,
      content: [
        'The competition organizers (Kolandium Organization) reserve the right to modify these rules at any time.',
        'All decisions regarding view counts, ratings, and prize winners are final.',
        'All submissions, including winning and non-winning entries, will remain available on the platform after the competition ends. However, all submissions from one competition session will not be eligible for subsequent competition sessions.',
        'Each competition session will have distinct start and end times, with a new round of eligible submissions.',
        'By submitting an entry, participants acknowledge that they have read, understood, and agree to abide by these official rules.'
      ]
    }
  ];

  return (
    <div
      className="max-w-4xl mx-auto p-6 min-h-screen"
      style={{
        '--primary-grey': '#6B7280',
        '--secondary-grey': '#9CA3AF',
        '--dark-grey': '#374151',
        '--light-grey': '#F3F4F6',
      } as React.CSSProperties}
    >
      

      {/* Rules Sections */}
      <div className="space-y-6">
        {sections.map((section, index) => {
          const isOpen = openSections[section.id] || false;
          return (
            <div key={section.id} className="rounded-lg shadow-md border overflow-hidden"
              style={{ background: 'white', borderColor: 'var(--light-grey)' }}>
              <button
                className="w-full p-4 flex items-center gap-3 focus:outline-none focus:ring-2 justify-between"
                style={{
                  background: 'linear-gradient(to right, var(--primary-grey), var(--dark-grey))',
                  color: 'white',
                  border: 'none',
                }}
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
                aria-controls={`section-content-${section.id}`}
                type="button"
              >
                <span className="flex items-center gap-3">
                  {section.icon}
                  <h3 className="text-xl font-bold">
                    {index + 1}. {section.title}
                  </h3>
                </span>
                <span className="ml-2">{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div className="p-6" id={`section-content-${section.id}`}> 
                  <div className="space-y-4">
                    {Array.isArray(section.content) && (section.content as Array<any>).map((item: any, itemIndex: number) => {
                      if (typeof item === 'string') {
                        return (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <span className="font-bold text-sm mt-1 min-w-[24px]" style={{ color: 'var(--primary-grey)' }}>
                              {index + 1}.{itemIndex + 1}.
                            </span>
                            <p className="leading-relaxed" style={{ color: 'var(--dark-grey)' }}>{item}</p>
                          </div>
                        );
                      } else if (item.subtitle) {
                        return (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <span className="font-bold text-sm mt-1 min-w-[24px]" style={{ color: 'var(--primary-grey)' }}>
                              {index + 1}.{itemIndex + 1}.
                            </span>
                            <div>
                              <p className="font-semibold mb-1" style={{ color: 'var(--secondary-grey)' }}>{item.subtitle}:</p>
                              <p className="leading-relaxed" style={{ color: 'var(--dark-grey)' }}>{item.text}</p>
                            </div>
                          </div>
                        );
                      } else if (item.highlight) {
                        return (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <span className="font-bold text-sm mt-1 min-w-[24px]" style={{ color: 'var(--primary-grey)' }}>
                              {index + 1}.{itemIndex + 1}.
                            </span>
                            <div style={{ background: 'var(--light-grey)', borderLeft: '4px solid var(--secondary-grey)' }} className="p-3 rounded-r-lg">
                              <span className="font-medium" style={{ color: 'var(--secondary-grey)' }}>{item.text}</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact Information */}
      <div className="mt-8 rounded-lg p-6 text-center border"
        style={{ background: 'white', color: 'var(--secondary-grey)', borderColor: 'var(--light-grey)' }}>
        <h3 className="text-xl font-bold mb-2">Questions?</h3>
        <p className="mb-2">For questions or clarifications regarding the competition rules, please contact:</p>
        <a 
          href="mailto:kolandium@outlook.com" 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors border"
          style={{ background: 'white', color: 'var(--primary-grey)', borderColor: 'var(--light-grey)' }}
        >
          <FileText className="w-4 h-4" style={{ color: 'var(--primary-grey)' }} />
          kolandium@outlook.com
        </a>
      </div>

      {/* Footer */}
      {/* <div className="mt-6 text-center text-sm" style={{ color: 'var(--primary-grey)' }}>
        <p>© 2024 Kolandium Organization. All rights reserved.</p>
      </div> */}
    </div>
  );
};

export default OfficialRules;